import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Context as ExchangeRatesContext } from '../../../../api/exchangeRates/context';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import { getDataByKey } from '../../../../utils/dataUtils';
import { EXCHANGE_RATE_DECIMAL_VIEW_NUMBER, taytanFormatNumberToString } from '../../../../utils/mathUtils';

const getCurrenciesByCode = (currenciesDataAsArray) => {
    return getDataByKey(currenciesDataAsArray, 'code');
};

const exchangeRateToText = (exchangeRate) => {
    return taytanFormatNumberToString(exchangeRate, EXCHANGE_RATE_DECIMAL_VIEW_NUMBER);
};

const useExchangeRates = (props) => {
    const { paramsForFetching } = props;
    const exchangeRatesContext = useContext(ExchangeRatesContext);
    const currenciesContext = useContext(CurrenciesContext);

    const [loading, setLoading] = useState(false);

    const fetch = useCallback(() => {
        // Skip fetch if data already loaded (from provider) and no specific params requested
        const needsExchangeRates = paramsForFetching || !exchangeRatesContext.data?.length;
        const needsCurrencies = !currenciesContext.dataAsArray?.length;
        if (!needsExchangeRates && !needsCurrencies) return;

        setLoading(true);
        const promises = [];
        if (needsExchangeRates) promises.push(exchangeRatesContext.get(paramsForFetching));
        if (needsCurrencies) promises.push(currenciesContext.get({}));
        Promise.all(promises)
            .catch((err) => {
                console.error('Failed to load exchange rates', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [exchangeRatesContext, paramsForFetching, currenciesContext]);

    useEffect(() => {
        fetch();
    }, [paramsForFetching]); // eslint-disable-line

    const currencyPairs = exchangeRatesContext.data;

    // creates a 2 dimensional hashmap for efficient O(1) lookup time complexity
    const dataStructure = useMemo(
        () =>
            currencyPairs.reduce((acc, nextPair) => {
                const baseId = nextPair.base._id;
                const quoteId = nextPair.quote._id;
                if (!acc[baseId]) {
                    acc[baseId] = {};
                }
                acc[baseId][quoteId] = nextPair;
                return acc;
            }, {}),
        [currencyPairs]
    );

    const getExchangeRate = useCallback(
        (baseId, quoteId) => {
            if (dataStructure?.[baseId]?.[quoteId]) {
                return dataStructure[baseId][quoteId].exchangeRateSell;
            } 
            else if (dataStructure?.[quoteId]?.[baseId]) {
                return 1 / dataStructure[quoteId][baseId].exchangeRateSell;
            }
            return 'N/A';
        },
        [dataStructure]
    );

    return {
        loading,
        currenciesAsArray: currenciesContext.dataAsArray,
        getExchangeRate,
        exchangeRatesContext,
        setLoading
    };
};
export default useExchangeRates;
export { getCurrenciesByCode, exchangeRateToText };
